import { and, eq, type InitialQueryBuilder, type Ref } from "@tanstack/db";

import type { WsPermission } from "@storyboard/web-client";
import { store } from "@/store/store";
import type { Permission } from "@/store/types";

function transformWsPermission(r: WsPermission): Permission {
  return {
    id: r.id,
    workspaceMembershipId: r.workspaceMembershipId ?? null,
    feedId: r.feedId ?? null,
    workflowItemId: r.workflowItemId ?? null,
    name: r.name,
    // Records can arrive through unchecked casts; normalize so the strict eq(enabled, true)
    // live queries hold even if a producer ever emits int-as-boolean flags.
    enabled: Boolean(r.enabled),
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? null,
    accountId: null,
    expiresAt: null,
  };
}

export async function bulkUpsertPermission(records: WsPermission[]) {
  if (records.length === 0) return;
  // The old ON CONFLICT SET preserved createdAt (and the never-synced accountId/expiresAt).
  store.upsert("permission", records.map(transformWsPermission), (existing, next) => ({
    ...existing,
    workspaceMembershipId: next.workspaceMembershipId,
    feedId: next.feedId,
    workflowItemId: next.workflowItemId,
    name: next.name,
    enabled: next.enabled,
    updatedAt: next.updatedAt,
  }));
}

export const myFeedPermissionsQuery =
  (currentFeedId: string, myCurrentWorkspaceMembershipId: string) => (q: InitialQueryBuilder) =>
    q
      .from({ permission: store.collection("permission") })
      .where(({ permission }) =>
        and(
          eq(permission.feedId, currentFeedId),
          eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
          eq(permission.enabled, true),
        ),
      );

export const feedPermissionsQuery = (feedId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ permission: store.collection("permission") })
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ permission, membership }) =>
      eq(permission.workspaceMembershipId, membership.id),
    )
    .where(({ permission, membership }) => and(eq(permission.feedId, feedId), eq(membership.status, "active")))
    // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`; the annotation keeps the row type `T`.
    .select(({ permission }): Ref<Permission> => permission);

export const feedPermissionSampleQuery = (feedId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ permission: store.collection("permission") })
    .where(({ permission }) => eq(permission.feedId, feedId))
    .orderBy(({ permission }) => permission.id)
    .limit(10)
    .select(({ permission }) => ({ id: permission.id }));

export const fetchFeedPermissionsQuery = (workspaceMembershipId: string, feedId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ permission: store.collection("permission") })
    .where(({ permission }) =>
      and(eq(permission.feedId, feedId), eq(permission.workspaceMembershipId, workspaceMembershipId)),
    )
    .orderBy(({ permission }) => permission.id)
    .limit(3)
    .select(({ permission }) => ({ name: permission.name, enabled: permission.enabled }));
